import React, { PureComponent } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import Immutable from 'immutable'
import PropTypes from 'prop-types'

import { marketProductAdded, marketProductRemoved } from 'actions/trackingKeys'
import configProducts from 'config/products'

import { ProductDetailModal } from '../ProductDetails'
import { ProductPresentation } from './Product.presentation'

import css from './Product.css'

const NUMBER_OF_COLUMN_CLASSES = {
  fourColumn: css.fourColumn,
}

const getProductImageMedia = (product) => {
  const images = Object.values(product.images || {}).filter((image) => image && image.src)

  return Immutable.fromJS(images)
}

const propTypes = {
  basket: PropTypes.instanceOf(Immutable.Map).isRequired,
  canEditOrder: PropTypes.bool.isRequired,
  product: PropTypes.shape({
    attributes: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        value: PropTypes.string,
        unit: PropTypes.string,
      }),
    ),
    description: PropTypes.string,
    id: PropTypes.string,
    title: PropTypes.string,
    listPrice: PropTypes.string,
    images: PropTypes.objectOf(
      PropTypes.shape({
        src: PropTypes.string,
      }),
    ),
    categories: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
        hidden: PropTypes.bool,
      }),
    ),
    ageRestricted: PropTypes.bool,
    quantity: PropTypes.number,
    stock: PropTypes.number,
  }).isRequired,
  limitReached: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      type: PropTypes.string,
      value: PropTypes.number,
    }),
  ]).isRequired,
  ageVerified: PropTypes.bool,
  ageVerificationPending: PropTypes.bool.isRequired,
  basketProductAdd: PropTypes.func.isRequired,
  basketProductRemove: PropTypes.func.isRequired,
  numberOfColumnClass: PropTypes.string,
  showCategoryTags: PropTypes.bool,
  temp: PropTypes.func.isRequired,
  orderConfirmationProductTracking: PropTypes.func.isRequired,
  isSelectedProduct: PropTypes.bool.isRequired,
  addProduct: PropTypes.bool.isRequired,
  toggleAgeVerificationPopUp: PropTypes.func.isRequired,
  productId: PropTypes.string,
  category: PropTypes.string,
  openProductModalTracking: PropTypes.func.isRequired,
}

const defaultProps = {
  ageVerified: false,
  productId: null,
  numberOfColumnClass: '',
  category: '',
  showCategoryTags: false,
}

class Product extends PureComponent {
  constructor() {
    super()
    this.state = {
      showDetailsScreen: false,
    }
  }

  componentDidUpdate(prevProps) {
    const { ageVerified, isSelectedProduct, addProduct, product, basketProductAdd } = this.props
    const ageVerifiedChanged = !Object.is(ageVerified, prevProps.ageVerified)

    if (isSelectedProduct && ageVerified && ageVerifiedChanged) {
      if (addProduct) {
        basketProductAdd(product.id, null, false)
        datadogRum.addAction('basket_add_product', {
          product_id: product.id,
        })
      } else {
        this.openDetailsVisibility()
      }
    }
  }

  openDetailsVisibility = () => {
    const { openProductModalTracking } = this.props
    const { showDetailsScreen } = this.state

    if (showDetailsScreen) return

    const trackingData = this.mapProductToTrackingData(
      'market_product_detail_view',
      'secondary_action',
    )
    openProductModalTracking(trackingData)

    this.setState({
      showDetailsScreen: true,
    })
  }

  closeDetailsVisibility = () => {
    this.setState({
      showDetailsScreen: false,
    })
  }

  toggleModal = () => {
    const { toggleAgeVerificationPopUp, product, temp } = this.props
    if (this.isAgeVerificationRequired()) {
      toggleAgeVerificationPopUp()
      temp('productId', product.id)
      temp('addProduct', false)
    } else {
      this.openDetailsVisibility()
    }
  }

  isAgeVerificationRequired = () => {
    const { product, ageVerified } = this.props
    const { ageRestricted } = product

    return !ageVerified && ageRestricted
  }

  mapProductToTrackingData = (eventName, eventType) => {
    const { product, category } = this.props
    const productProperties = { ...product }

    delete productProperties.media
    delete productProperties.images
    delete productProperties.categories
    delete productProperties.tags
    productProperties.category = category

    return { eventName, eventAction: 'clicked', eventType, eventProperties: { productProperties } }
  }

  onAddProduct = () => {
    const {
      product,
      basketProductAdd,
      limitReached,
      toggleAgeVerificationPopUp,
      temp,
      orderConfirmationProductTracking,
    } = this.props

    const { id } = product

    const isAgeVerificationRequired = this.isAgeVerificationRequired()
    if (!limitReached) {
      if (isAgeVerificationRequired) {
        toggleAgeVerificationPopUp()
        temp('productId', id)
        temp('addProduct', true)
      } else {
        basketProductAdd(id, null, false)
        datadogRum.addAction('basket_add_product', {
          product_id: id,
        })
        const trackingData = this.mapProductToTrackingData(marketProductAdded, 'primary_action')
        orderConfirmationProductTracking(trackingData)
      }
    }
  }

  onRemoveProduct = () => {
    const { product, basketProductRemove, orderConfirmationProductTracking } = this.props
    const { id } = product
    basketProductRemove(id)
    const trackingData = this.mapProductToTrackingData(marketProductRemoved, 'primary_action')
    orderConfirmationProductTracking(trackingData)
  }

  getProductCardContent = () => {
    const { product, basket, limitReached, ageVerificationPending, productId, showCategoryTags } =
      this.props
    const { id, title, listPrice, stock, categories = [] } = product
    const quantity = basket && basket.get('products').has(id) ? basket.getIn(['products', id]) : 0

    const imgSource = getProductImageMedia(product)
    const lowStock = stock <= configProducts.lowStockThreshold
    const outOfStock = stock <= 0
    const categoryTags = showCategoryTags
      ? categories.map((productCategory) => productCategory?.title).filter(Boolean)
      : []

    const inProgress = ageVerificationPending && id === productId

    return {
      categoryTags,
      id,
      title,
      lowStock,
      outOfStock,
      listPrice,
      imgSource,
      limitReached,
      ageVerificationPending,
      qty: quantity,
      openDetailsScreen: this.toggleModal,
      inProgress,
    }
  }

  getProductDetails = () => {
    const { product } = this.props

    return {
      ...product,
      media: getProductImageMedia(product),
      showPopUp: true,
    }
  }

  render() {
    const { showDetailsScreen } = this.state
    const { canEditOrder, product, toggleAgeVerificationPopUp, numberOfColumnClass } = this.props
    const productCardContent = this.getProductCardContent()
    const productDetails = this.getProductDetails()

    return (
      <section
        className={`${css.productWrapper} ${NUMBER_OF_COLUMN_CLASSES[numberOfColumnClass]}`}
        data-testing="marketProduct"
      >
        {/* eslint-disable react/jsx-props-no-spreading */}
        <ProductPresentation
          onAdd={this.onAddProduct}
          onRemove={this.onRemoveProduct}
          toggleAgeVerificationPopUp={toggleAgeVerificationPopUp}
          {...productCardContent}
          canEditOrder={canEditOrder}
        />
        {showDetailsScreen && (
          <ProductDetailModal
            ageRestricted={product.ageRestricted}
            attributes={product.attributes}
            description={product.description}
            imageMedia={productDetails.media}
            id={product.id}
            isOpen
            listPrice={product.listPrice}
            onVisibilityChange={this.closeDetailsVisibility}
            onAdd={this.onAddProduct}
            onRemove={this.onRemoveProduct}
            canEditOrder={canEditOrder}
            title={product.title}
          />
        )}
        {/* eslint-enable react/jsx-props-no-spreading */}
      </section>
    )
  }
}

Product.propTypes = propTypes

Product.defaultProps = defaultProps

export { Product }
